import type { ReactNode } from "react";
import { useEffect, useMemo, useRef } from "react";
import { Form, Link, useSearchParams } from "react-router";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Cross2Icon, MagnifyingGlassIcon } from "@radix-ui/react-icons";

import { useURL } from "~/contexts";
import { cn, useDebounce } from "~/lib/ui";
import { useRootLayoutData } from "~/root";

import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import { useSearchContext } from "./search-context";

export const MeganavSearchBar = ({
  className,
  suggestions,
  isMobile,
}: {
  className?: string;
  suggestions?: (text: string) => ReactNode;
  isMobile?: boolean;
}) => {
  const [searchParams] = useSearchParams();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const url = useURL();
  const { DEFAULT_SEARCH_TERM } = useRootLayoutData();
  const {
    searchState,
    setSearchState,
    isDialogVisible,
    setIsDialogVisible,
    isSuggestionsOpen,
    setIsSuggestionsOpen,
  } = useSearchContext();

  const searchParamUrl =
    searchParams.get("q") && searchParams.get("q") !== DEFAULT_SEARCH_TERM
      ? searchParams.get("q")
      : "";

  const closeSearch = (e?: MouseEvent) => {
    if (e && e.target instanceof Element) {
      const closestAnchor = e.target.closest("a");

      const isViewResultsBtnClicked = closestAnchor?.id === "view-all-results";
      const isSuggestionClicked = closestAnchor?.id === "search-suggestion";
      if (
        (searchContainerRef?.current?.contains(e.target) &&
          isSuggestionClicked) ||
        isViewResultsBtnClicked
      ) {
        setIsSuggestionsOpen(false);
        setIsDialogVisible(false);
      }
    }
  };

  useEffect(() => {
    const blurrableElements = document.querySelectorAll("#blurrable-content");
    const headerContent = document.querySelector("#nav-content");

    if (blurrableElements.length && !isDialogVisible)
      if (blurrableElements.length && !isDialogVisible)
        blurrableElements.forEach(
          e => e.classList.remove("blur", "pointer-events-none"),
          headerContent?.classList.remove(
            "hover:bg-transparent",
            "focus-within:bg-transparent",
          ),
        );

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter") {
        setIsDialogVisible(false);
      }
    };

    document.addEventListener("keydown", onKey);
    document.addEventListener("click", closeSearch);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("click", closeSearch);
    };
  }, [isDialogVisible]);

  useEffect(() => {
    if (isDialogVisible) {
      setIsSuggestionsOpen(true);
      setSearchState({
        value: searchParamUrl || DEFAULT_SEARCH_TERM,
      });
      setTimeout(() => {
        requestAnimationFrame(() => {
          searchInputRef?.current?.focus();
        });
      }, 0);
    }
  }, [isDialogVisible]);

  const debouncedValue = useDebounce(searchState.value.trim(), 120);
  const debouncedLink = url(
    debouncedValue && `/search?q=${encodeURIComponent(debouncedValue)}`,
  );

  const suggestionsComponent = useMemo(() => {
    if (!suggestions) return null;
    return suggestions(debouncedValue);
  }, [suggestions, debouncedValue]);

  const clearInput = (focusInput = true) => {
    if (searchInputRef?.current) {
      searchInputRef.current.value = "";
      focusInput && searchInputRef.current.focus();
    }
    setIsSuggestionsOpen(true);
    setSearchState(() => ({
      value: DEFAULT_SEARCH_TERM,
    }));
  };
  return (
    <div
      className={cn(
        "top-0 z-20 mx-auto flex text-xs transition-height sm-max:block",
        isMobile && "animate-reveal",
        className,
      )}
    >
      <div className="relative left-0 flex flex-col items-center justify-items-center">
        <dl className="flex sm-max:block sm-max:w-full">
          <dt className="sr-only">Search</dt>
          <dd className="relative">
            <Button
              aria-label="Search"
              variant="linkInline"
              type="submit"
              onClick={(
                event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
              ) => {
                event.preventDefault();
                setIsDialogVisible(true);
                searchInputRef.current?.focus();
              }}
            >
              <MagnifyingGlassIcon
                className="h-5 w-5 transition-[background-color]"
                aria-hidden="true"
              />
            </Button>
            <Dialog
              open={isDialogVisible}
              onOpenChange={() => {
                setIsDialogVisible(false);
              }}
              modal={true}
            >
              <DialogContent
                forceMount
                className="z-50 flex h-full w-full max-w-none flex-col items-start overflow-y-auto bg-white p-6 py-0 shadow-lg duration-200 md:left-auto md:right-0 md:w-[33rem] md:translate-x-0"
                closeIconStyle="hidden"
              >
                <div className="sticky top-0 z-50 flex w-full gap-1 bg-white pb-4 pt-6">
                  <DialogPrimitive.Close
                    className={cn(
                      "flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-md ring-offset-background transition-opacity hover:opacity-100 focus:outline-none",
                      "left-0 top-[2rem] rounded-none shadow-none",
                    )}
                  >
                    <Cross2Icon className={`h-[24px] w-[24px]`} />
                    <span className="sr-only">Close</span>
                  </DialogPrimitive.Close>
                  <Form
                    method="get"
                    action={url("/search")}
                    className="relative flex w-full items-center"
                  >
                    <div className="absolute left-3 top-1/2 flex -translate-y-1/2 items-center sm:left-4">
                      <Button
                        variant="linkInline"
                        className="no-underline underline-offset-0 hover:no-underline"
                        type="submit"
                        onClick={() => {
                          setIsDialogVisible(false);
                        }}
                      >
                        <MagnifyingGlassIcon
                          className="h-5 w-5 transition-[background-color]"
                          aria-hidden="true"
                        />
                      </Button>
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      maxLength={50}
                      onChange={e => {
                        const newValue = e.target.value;
                        if (newValue.trim() === "") {
                          setSearchState({ value: DEFAULT_SEARCH_TERM });
                        } else {
                          setSearchState({ value: newValue });
                        }
                      }}
                      id="search"
                      value={
                        searchState.value !== DEFAULT_SEARCH_TERM
                          ? searchState.value
                          : ""
                      }
                      autoComplete="off"
                      className={`xs:text-sm h-10 w-full rounded-full border border-gray-300 bg-gray-200 pl-12 pr-16 text-xs text-neutral-900 focus:outline-none md:text-base`}
                      placeholder="Search for a product..."
                    />
                    {/* Hidden input for submitting default search term if input is empty */}
                    <input
                      type="hidden"
                      name="q"
                      value={
                        searchState.value !== DEFAULT_SEARCH_TERM
                          ? searchState.value
                          : DEFAULT_SEARCH_TERM
                      }
                    />
                  </Form>
                  <div className="absolute right-7 top-1/2">
                    <Button
                      variant="linkInline"
                      onClick={() => {
                        clearInput();
                      }}
                      className="no-underline underline-offset-0 hover:no-underline"
                    >
                      <p className="text-xs font-normal text-neutral-400">
                        Clear
                      </p>
                    </Button>
                  </div>
                  {debouncedLink && (
                    <Link
                      to={debouncedLink}
                      prefetch="render"
                      className="hidden"
                    >
                      Search: {debouncedLink}
                    </Link>
                  )}
                </div>
                {suggestionsComponent && isSuggestionsOpen && (
                  <div
                    className="flex w-full flex-col rounded-lg bg-page-background"
                    ref={searchContainerRef}
                  >
                    {suggestionsComponent}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </dd>
        </dl>
      </div>
    </div>
  );
};
MeganavSearchBar.displayName = "Meganav.MeganavSearchBar";
